import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DownloadOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';

import { ModalPdf } from '../../../PdfModal';
import { getReviewId } from '../../_hooks/getReviewId';
import { usePDFGenerate } from './hooks/usePDFGenerate';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetResolution886ReviewResults } from '@/hooks/v2/resolution-886';
import * as S from './styles';

interface ButtonsCustomProps {
	isLoadingPDF: boolean;
	consolidated: boolean;
	hasCustomModal: boolean;
	hasConsolidated: boolean;
	handleConsolidatePDF(values: { file: File | null; status: boolean; include_fields?: string[] }): void;
}

export function ButtonsCustom({
	isLoadingPDF,
	consolidated,
	hasConsolidated,
	handleConsolidatePDF
}: Readonly<ButtonsCustomProps>) {
	const [isOpenModalPDF, setIsOpenModalPDF] = useState<boolean>(false);
	const [isConsolidateMode, setIsConsolidateMode] = useState<boolean>(false);
	const { handleSetReviewConsolidated, lastReviewId } = useResolution886Report();

	const { organization, company } = useApplicationContext();
	const { report, selectedReviewId } = useResolution886Report();

	const actualReviewId = getReviewId(selectedReviewId, report?.reviews || []);
	const canConsolidate = actualReviewId === lastReviewId;

	const { handleDownloadPDF, downloadPDFLoading } = usePDFGenerate({
		report,
		organizationId: organization?.id!,
		companyId: company?.id!,
		lastReviewId: actualReviewId
	});

	const { data: reviewResults } = useGetResolution886ReviewResults({
		organization_id: organization?.id!,
		company_id: company?.id!,
		report_id: report?.id!,
		review_id: actualReviewId
	});

	const isConsolidatedAndHasLocation = consolidated && reviewResults?.location !== null;

	async function handleOpenModal(): Promise<void> {
		if (consolidated) {
			await handleDownloadPDF({ pdf_custom: [] });
			return;
		}
		setIsConsolidateMode(false);
		setIsOpenModalPDF(true);
	}

	function handleCloseModal(): void {
		setIsOpenModalPDF(false);
		setIsConsolidateMode(false);
	}

	async function handleOnSubmit(values: { pdf_custom: string[] }): Promise<void> {
		await handleDownloadPDF(values);

		handleCloseModal();
	}

	async function handleConsolidateWithPDF(values: { pdf_custom: string[] }): Promise<void> {
		handleConsolidatePDF({
			file: null,
			status: !consolidated,
			include_fields: values.pdf_custom || []
		});
		handleCloseModal();
	}

	useEffect(() => {
		const isConsolidated = Number(consolidated) === 0 ? false : true;
		handleSetReviewConsolidated(isConsolidated);
	}, [consolidated]);

	async function handleConsolidate(): Promise<void> {
		handleConsolidatePDF({
			file: null,
			status: !consolidated,
			include_fields: []
		});
		setIsOpenModalPDF(false);
		setIsConsolidateMode(true);
	}

	return (
		<Row justify="end" align="middle" gutter={[30, 0]}>
			<Col>
				<S.DownloadButton
					ghost
					size="large"
					style={{ backgroundColor: '#fff' }}
					type="primary"
					loading={isLoadingPDF || downloadPDFLoading}
					icon={<DownloadOutlined />}
					onClick={handleOpenModal}
				>
					{I18n.get('Download PDF')}
				</S.DownloadButton>
			</Col>
			{hasConsolidated && canConsolidate && (
				<Col>
					{!consolidated ? (
						<S.ConsolidatedButton
							ghost
							size="large"
							type="primary"
							style={{ backgroundColor: '#fff' }}
							loading={isLoadingPDF}
							icon={<LockOutlined style={{ color: '#2cc852' }} />}
							onClick={handleConsolidate}
						>
							{I18n.get('Consolidate report')}
						</S.ConsolidatedButton>
					) : (
						<S.DesconsolidatedButton
							ghost
							size="large"
							type="primary"
							loading={isLoadingPDF}
							style={{ backgroundColor: '#fff' }}
							icon={<UnlockOutlined style={{ color: '#D13342' }} />}
							onClick={handleConsolidate}
						>
							{I18n.get('Deconsolidate the report')}
						</S.DesconsolidatedButton>
					)}
				</Col>
			)}
			<ModalPdf
				open={isOpenModalPDF}
				loading={downloadPDFLoading}
				onClose={handleCloseModal}
				onSubmit={isConsolidateMode ? handleConsolidateWithPDF : handleOnSubmit}
				onShowFieldSelection={isConsolidatedAndHasLocation}
			/>
		</Row>
	);
}
